"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { ArrowUpRight, LayoutDashboard } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { authClient } from "@/shared/config/authClient.config";
import { getInitials } from "@/shared/lib/utils";
type Props = {
  // Closes the drawer once a link is tapped (mirrors the old setOpen(false)).
  onNavigate: () => void;
};

// Session-aware auth block for the mobile drawer. Same logged-in/out logic as
// the desktop cluster, but laid out as full-width stacked buttons.
export const NavAuthButtonsMobile = t0 => {
  const $ = _c(40);
  const {
    onNavigate
  } = t0;
  const {
    data: session,
    isPending
  } = authClient.useSession();
  if (isPending) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <><Skeleton className="h-12 w-full rounded-xl" /><Skeleton className="h-12 w-full rounded-xl" /></>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  const user = session?.user;
  if (!user) {
    let t1;
    if ($[1] !== onNavigate) {
      t1 = <Button nativeButton={false} variant="outline" size="lg" className="h-12 w-full rounded-xl text-base font-semibold" render={<Link href="/login" onClick={onNavigate} />}>Sign In</Button>;
      $[1] = onNavigate;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] !== onNavigate) {
      t2 = <Link href="/register" onClick={onNavigate} />;
      $[3] = onNavigate;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <ArrowUpRight className="size-5" />;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t2) {
      t4 = <Button nativeButton={false} size="lg" className="h-12 w-full rounded-xl text-base font-semibold" render={t2}>Get Started{t3}</Button>;
      $[6] = t2;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <p className="pt-2 text-center text-xs text-zinc-400">Run your whole gym from one dashboard.</p>;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== t1 || $[10] !== t4) {
      t6 = <>{t1}{t4}{t5}</>;
      $[9] = t1;
      $[10] = t4;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  const t1 = user.image ?? undefined;
  let t2;
  if ($[12] !== t1 || $[13] !== user.name) {
    t2 = <AvatarImage src={t1} alt={user.name} />;
    $[12] = t1;
    $[13] = user.name;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  let t3;
  if ($[15] !== user.name) {
    t3 = getInitials(user.name);
    $[15] = user.name;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  let t4;
  if ($[17] !== t3) {
    t4 = <AvatarFallback className="bg-primary/15 text-sm font-bold text-primary">{t3}</AvatarFallback>;
    $[17] = t3;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== t2 || $[20] !== t4) {
    t5 = <Avatar className="size-10">{t2}{t4}</Avatar>;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== user.name) {
    t6 = <p className="truncate text-sm font-semibold text-zinc-900">{user.name}</p>;
    $[22] = user.name;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== user.email) {
    t7 = <p className="truncate text-xs text-zinc-500">{user.email}</p>;
    $[24] = user.email;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t6 || $[27] !== t7) {
    t8 = <div className="min-w-0">{t6}{t7}</div>;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== t5 || $[30] !== t8) {
    t9 = <div className="flex items-center gap-3 rounded-xl border border-zinc-100 px-3 py-3">{t5}{t8}</div>;
    $[29] = t5;
    $[30] = t8;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== onNavigate) {
    t10 = <Link href="/dashboard" onClick={onNavigate} />;
    $[32] = onNavigate;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <LayoutDashboard className="size-5" />;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== t10) {
    t12 = <Button nativeButton={false} size="lg" className="h-12 w-full rounded-xl text-base font-semibold" render={t10}>{t11}Dashboard</Button>;
    $[35] = t10;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== t12 || $[38] !== t9) {
    t13 = <>{t9}{t12}</>;
    $[37] = t12;
    $[38] = t9;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  return t13;
};